import React, { useState, useEffect } from "react";
import { useOrganization } from "@/hooks/useOrganization";
import { StandardPageHeader } from "@/components/layout/StandardPageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NotificationForm } from "@/components/notifications/NotificationForm";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { motion } from "framer-motion";
import { 
  Bell, 
  Send, 
  History, 
  Users, 
  Calendar,
  MessageSquare,
  AlertCircle,
  Megaphone,
  Clock,
  CheckCircle
} from "lucide-react";
import { cn } from "@/lib/utils";

// Types for notifications
interface Notification {
  id: string;
  content: string;
  type: string;
  created_at: string;
  title?: string;
  link?: string;
  status: string;
  created_by: string;
  count: number;
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const Notifications = () => {
  const { organization } = useOrganization();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("send");

  useEffect(() => {
    if (organization?.id) {
      fetchNotifications();
    }
  }, [organization?.id]);

  const fetchNotifications = async () => {
    if (!organization?.id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("notifications")
        .select(`
          id,
          content,
          type,
          created_at,
          created_by,
          status,
          title,
          link,
          targets:notification_targets(target_type, target_id)
        `)
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (data) {
        const notificationsWithCounts = await Promise.all(
          data.map(async (notification) => {
            const { count } = await supabase
              .from("user_notifications")
              .select("*", { count: "exact", head: true })
              .eq("notification_id", notification.id);

            return {
              ...notification,
              count: count || 0,
            };
          })
        );

        setNotifications(notificationsWithCounts);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSent = () => {
    fetchNotifications();
    setActiveTab("history");
  };

  // Get notification type styling and icon
  const getNotificationTypeInfo = (type: string) => {
    const typeInfo: Record<string, { 
      color: string; 
      bgColor: string; 
      icon: React.ComponentType<any>;
      gradient: string;
    }> = {
      announcement: { 
        color: "text-blue-700", 
        bgColor: "bg-blue-50 border-blue-200", 
        icon: Megaphone,
        gradient: "from-blue-500 to-indigo-600"
      },
      reminder: { 
        color: "text-purple-700", 
        bgColor: "bg-purple-50 border-purple-200", 
        icon: Clock,
        gradient: "from-purple-500 to-violet-600"
      },
      alert: { 
        color: "text-red-700", 
        bgColor: "bg-red-50 border-red-200", 
        icon: AlertCircle,
        gradient: "from-red-500 to-rose-600"
      },
      payment: { 
        color: "text-green-700", 
        bgColor: "bg-green-50 border-green-200", 
        icon: CheckCircle,
        gradient: "from-green-500 to-emerald-600"
      },
      registration: { 
        color: "text-amber-700", 
        bgColor: "bg-amber-50 border-amber-200", 
        icon: Users,
        gradient: "from-amber-500 to-orange-600"
      },
      attendance: { 
        color: "text-indigo-700", 
        bgColor: "bg-indigo-50 border-indigo-200", 
        icon: Calendar,
        gradient: "from-indigo-500 to-blue-600"
      },
      message: { 
        color: "text-slate-700", 
        bgColor: "bg-slate-50 border-slate-200", 
        icon: MessageSquare,
        gradient: "from-slate-500 to-gray-600"
      },
    };

    return typeInfo[type] || { 
      color: "text-gray-700", 
      bgColor: "bg-gray-50 border-gray-200", 
      icon: Bell,
      gradient: "from-gray-500 to-slate-600"
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/40">
      <StandardPageHeader
        title="Notifications"
        subtitle="Send and manage notifications for your organization"
        icon={Bell}
      />
      
      <PageContainer maxWidth="full">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
        <motion.div variants={itemVariants}>
          <Tabs
            defaultValue="send"
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-6"
          >
            <TabsList className="grid w-full grid-cols-2 bg-white/50 backdrop-blur-sm border border-gray-200/50">
              <TabsTrigger 
                value="send" 
                className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FA8C00] data-[state=active]:to-[#FA8C00]/90 data-[state=active]:text-white"
              >
                <Send className="h-4 w-4" />
                <span className="hidden sm:inline">Send Notification</span>
                <span className="sm:hidden">Send</span>
              </TabsTrigger>
              <TabsTrigger 
                value="history"
                className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4ba3c7] data-[state=active]:to-[#4ba3c7]/90 data-[state=active]:text-white"
              >
                <History className="h-4 w-4" />
                <span className="hidden sm:inline">History</span>
                <span className="sm:hidden">History</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="send" className="space-y-6">
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-orange-50 via-white to-amber-50"
              >
                <Card className="border-orange-200/50 shadow-lg">
                  <CardHeader className="text-center pb-4">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <motion.div 
                        className="bg-gradient-to-r from-[#FA8C00] to-[#FA8C00]/90 p-3 rounded-xl"
                        whileHover={{ rotate: 5, scale: 1.05 }}
                      >
                        <Send className="h-6 w-6 text-white" />
                      </motion.div>
                      <CardTitle className="text-2xl">Send Notification</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      Compose and send notifications to your organization members
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {organization && (
                      <NotificationForm
                        organizationId={organization.id}
                        onSuccess={handleNotificationSent}
                      />
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </TabsContent>

            <TabsContent value="history" className="space-y-6">
              <motion.div variants={itemVariants}>
                <Card className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-blue-200/50 shadow-lg">
                  <CardHeader className="text-center pb-4">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <motion.div 
                        className="bg-gradient-to-r from-[#4ba3c7] to-[#4ba3c7]/90 p-3 rounded-xl"
                        whileHover={{ rotate: 5, scale: 1.05 }}
                      >
                        <History className="h-6 w-6 text-white" />
                      </motion.div>
                      <CardTitle className="text-2xl">Notification History</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      View and track all notifications sent to your organization
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex justify-center py-12">
                        <motion.div 
                          className="flex items-center gap-3"
                          animate={{ opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#4ba3c7]"></div>
                          <span className="text-gray-600">Loading notifications...</span>
                        </motion.div>
                      </div>
                    ) : notifications.length === 0 ? (
                      <EmptyState
                        icon={Bell}
                        title="No notifications yet"
                        description="You haven't sent any notifications yet. Create your first notification to see it here."
                      >
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button 
                            onClick={() => setActiveTab("send")}
                            className="bg-gradient-to-r from-[#FA8C00] to-[#FA8C00]/90 hover:from-[#FA8C00]/90 hover:to-[#FA8C00]/80"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Send a notification
                          </Button>
                        </motion.div>
                      </EmptyState>
                    ) : (
                      <div className="space-y-4">
                        {/* Mobile-friendly notification cards */}
                        <div className="space-y-4 lg:hidden">
                          {notifications.map((notification, index) => {
                            const typeInfo = getNotificationTypeInfo(notification.type);
                            const IconComponent = typeInfo.icon;
                            
                            return (
                              <motion.div
                                key={notification.id}
                                variants={itemVariants}
                                initial="hidden"
                                animate="visible"
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ y: -2, scale: 1.01 }}
                              >
                                <Card className={cn("border transition-all duration-200", typeInfo.bgColor)}>
                                  <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                      <div className={cn("p-2 rounded-lg bg-gradient-to-r", typeInfo.gradient)}>
                                        <IconComponent className="h-4 w-4 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge 
                                            variant="secondary" 
                                            className={cn("text-xs", typeInfo.color)}
                                          >
                                            {notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
                                          </Badge>
                                          <span className="text-xs text-gray-500">
                                            {format(new Date(notification.created_at), "MMM d, h:mm a")}
                                          </span>
                                        </div>
                                        {notification.title && (
                                          <h4 className="font-medium text-sm mb-1 text-gray-900">
                                            {notification.title}
                                          </h4>
                                        )}
                                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                          {notification.content}
                                        </p>
                                        {notification.link && (
                                          <p className="text-xs text-blue-600 truncate mb-2">
                                            {notification.link}
                                          </p>
                                        )}
                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                          <Users className="h-3 w-3" />
                                          <span>{notification.count} recipients</span>
                                        </div>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            );
                          })}
                        </div>

                        {/* Desktop table view */}
                        <div className="hidden lg:block bg-white rounded-xl border border-gray-200/50 overflow-hidden shadow-sm">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                                <tr>
                                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Date
                                  </th>
                                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Type
                                  </th>
                                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Content
                                  </th>
                                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Recipients
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {notifications.map((notification, index) => {
                                    const typeInfo = getNotificationTypeInfo(notification.type);
                                    const IconComponent = typeInfo.icon;
                                    
                                    return (
                                      <motion.tr
                                        key={notification.id}
                                        variants={itemVariants}
                                        initial="hidden"
                                        animate="visible"
                                        transition={{ delay: index * 0.05 }}
                                        className="hover:bg-gray-50/50 transition-colors duration-200"
                                      >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                          {format(new Date(notification.created_at), "MMM d, yyyy h:mm a")}
                                        </td>
                                        <td className="px-6 py-4">
                                          <div className="flex items-center gap-2">
                                            <div className={cn("p-1.5 rounded-md bg-gradient-to-r", typeInfo.gradient)}>
                                              <IconComponent className="h-3 w-3 text-white" />
                                            </div>
                                            <Badge variant="secondary" className={cn("text-xs", typeInfo.color)}>
                                              {notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
                                            </Badge>
                                          </div>
                                        </td>
                                        <td className="px-6 py-4">
                                          {notification.title && (
                                            <div className="font-medium text-gray-900 mb-1">
                                              {notification.title}
                                            </div>
                                          )}
                                          <div className="text-sm text-gray-600 max-w-md">
                                            {notification.content}
                                          </div>
                                          {notification.link && (
                                            <div className="text-xs text-blue-600 truncate mt-1 max-w-md">
                                              {notification.link}
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                                          <div className="flex items-center justify-end gap-1">
                                            <Users className="h-4 w-4 text-gray-400" />
                                            <span>{notification.count}</span>
                                          </div>
                                        </td>
                                      </motion.tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </TabsContent>
          </Tabs>
        </motion.div>
        </motion.div>
      </PageContainer>
    </div>
  );
};

export default Notifications;